// Port of Flutter's screenRedirector() (homeScreenFunctions.dart) — pick the
// control screen for a device from its DeviceID.
//
// The Dart builds a 14-entry ScreenArray and indexes into it; the same mapping
// lives in core/devices/deviceMeta.js as deviceTypeKey(), so both UIs and the
// scenes/wall layers agree on what a device is.
//
// Device rows arrive via nav state when opened from a list; opening the route
// directly (deep link, or the master "Test Device" prompt) fetches the row.
//
// PORT STATUS: every device type Flutter routes to now has a Flutter-faithful
// mobile screen. The `default` branch below is reached only by a DeviceID that
// screenRedirector itself does not recognise (its UnsupportedDeviceScreen slot),
// and falls back to the generic React controls.
//
// bellTouchScreen.dart needs no port: DeviceID 5 is in the one-touch ID list, so
// screenRedirector sends it to oneTouchScreen and the bell screen is unreachable
// dead code in the Flutter app too.

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import TouchScreen from './TouchScreen';
import FanScreen from './FanScreen';
import PhaseScreen from './PhaseScreen';
import LightScreen from './LightScreen';
import BleNodeScreen from './BleNodeScreen';
import CurtainScreen from './CurtainScreen';
import DoorScreen from './DoorScreen';
import SensorScreen from './SensorScreen';
import IrRemoteScreen from './IrRemoteScreen';
import DeviceAppBar from './components/DeviceAppBar';
import { getDeviceDetail } from '../../../../core/api/api';
import { deviceTypeKey, relayCount, fanCount } from '../../../../core/devices/deviceMeta';
import { isSensorDevice } from '../../../../core/devices/sensorState';
import DesktopDeviceControls from '../../../desktop/pages/device/DeviceControls';
import { SceneControllerBody } from '../../../shared/SceneControllerPanel';

export default function MobileDeviceScreen() {
  const theme = useTheme();
  const { uniqueNumber } = useParams();
  const { state } = useLocation();
  const [device, setDevice] = useState(state?.device || null);
  const [loading, setLoading] = useState(!state?.device);

  useEffect(() => {
    if (device || !uniqueNumber) return undefined;
    let alive = true;
    setLoading(true);
    getDeviceDetail(uniqueNumber)
      .then((res) => {
        const row = Array.isArray(res.data) ? res.data[0] : res.data;
        if (alive && row) setDevice(row);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [device, uniqueNumber]);

  if (loading || !device) {
    return (
      <Box sx={{
        height: '100%', overflowY: 'auto', bgcolor: theme.sli.back,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      >
        <CircularProgress sx={{ color: theme.sli.text }} />
      </Box>
    );
  }

  const key = deviceTypeKey(device.DeviceID);

  // ⚠️ BEFORE the switch, and deliberately so. A sensor has no outputs, so the
  // `default` branch's generic controls build nothing and the screen reads
  // "No controllable outputs found." — about a device that is working. Anything
  // with a control screen of its own is unaffected: isSensorDevice requires the
  // type to have no screen at all. See core/devices/sensorState.
  if (isSensorDevice(device)) return <SensorScreen device={device} />;

  switch (key) {
    case 'oneTouch':
      // Single relay: no All bars — there is nothing to "all".
      return <TouchScreen device={device} relayCount={1} showAllBars={false} />;
    case 'twoTouch':
      return <TouchScreen device={device} relayCount={2} tall />;
    case 'fourTouch':
      return <TouchScreen device={device} relayCount={4} />;
    case 'fan':
      return <FanScreen device={device} />;
    case 'singlePhase':
      return <PhaseScreen device={device} />;
    case 'threePhase':
      return <PhaseScreen device={device} threePhase />;
    case 'rgb':
      return <LightScreen device={device} variant="rgb" />;
    case 'wwh':
      return <LightScreen device={device} variant="wwh" />;
    // A white/warm light driven by BLE advertising from this phone, not MQTT.
    case 'bleWwh':
      return <BleNodeScreen device={device} />;
    case 'wwrgb':
      return <LightScreen device={device} variant="wwrgb" />;
    case 'curtain':
      return <CurtainScreen device={device} />;
    case 'door':
      return <DoorScreen device={device} />;
    case 'irBlaster':
      return <IrRemoteScreen device={device} />;
    // Four scene buttons — the room's scenes are the controls; nothing to switch.
    case 'sceneController':
      return (
        <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: theme.sli.back }}>
          <DeviceAppBar device={device} />
          <SceneControllerBody device={device} />
        </Box>
      );
    // Any panel of N plain switches the catalogue knows about — the new glass
    // modules (94–99) and everything else that used to fall to generic toggles.
    case 'touch':
      return <TouchScreen device={device} relayCount={relayCount(device.DeviceID)} />;
    case 'switchFan':
      // A switch bank with fan channels after it — 4+1, 7+1, 5+2. Both counts
      // come from the catalogue, so a 5+2 draws five tiles and TWO dials.
      return (
        <TouchScreen
          device={device}
          relayCount={relayCount(device.DeviceID)}
          fanChannels={fanCount(device.DeviceID)}
        />
      );
    default:
      return (
        <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: theme.sli.back }}>
          <DeviceAppBar device={device} />
          <Box sx={{ p: 1.5 }}>
            <DesktopDeviceControls device={device} uniqueNumber={uniqueNumber} embedded />
          </Box>
        </Box>
      );
  }
}
